/* ==========================================================================
   FILE: modules/identity/components/IINDisplay.jsx
   IAMONEAI - IIN Display Component
   Formats and displays the Identity Number with copy functionality
   ========================================================================== */

import React, { useState, useCallback } from 'react';
import './IINDisplay.css';

// ==========================================================================
// IIN DISPLAY COMPONENT
// ==========================================================================

/**
 * Displays IIN with formatting, masking, and copy functionality
 * 
 * @param {Object} props
 * @param {string} iin - The IIN to display (16 chars: XXXX-XXXX-XXXX-XXXX)
 * @param {boolean} masked - Whether to mask middle characters
 * @param {boolean} copyable - Whether to show copy button
 * @param {string} size - Display size: 'sm' | 'md' | 'lg'
 * @param {boolean} showLabel - Whether to show "IIN" label
 */
function IINDisplay({
  iin,
  masked = false,
  copyable = true,
  size = 'md',
  showLabel = false,
  className = '',
}) {
  const [copied, setCopied] = useState(false);

  // Format IIN with dashes
  const formattedIIN = formatIIN(iin, masked);

  // Handle copy
  const handleCopy = useCallback(async () => {
    if (!iin || copied) return;

    try {
      await navigator.clipboard.writeText(iin.replace(/-/g, ''));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy IIN:', err);
    }
  }, [iin, copied]);

  if (!iin) {
    return (
      <div className={`iin-display iin-display-empty ${className}`}>
        <span className="iin-placeholder">No IIN assigned</span>
      </div>
    );
  }

  const displayClasses = [
    'iin-display',
    `iin-display-${size}`,
    copyable && 'iin-display-copyable',
    className,
  ].filter(Boolean).join(' ');

  return (
    <div className={displayClasses}>
      {showLabel && <span className="iin-label">IIN</span>}
      
      <div className="iin-value-wrapper" onClick={copyable ? handleCopy : undefined}>
        <code className="iin-value">
          {formattedIIN.split('-').map((segment, index) => (
            <React.Fragment key={index}>
              {index > 0 && <span className="iin-separator">-</span>}
              <span className={`iin-segment ${masked && index > 0 && index < 3 ? 'iin-segment-masked' : ''}`}>
                {segment}
              </span>
            </React.Fragment>
          ))}
        </code>

        {copyable && (
          <button
            className={`iin-copy-btn ${copied ? 'iin-copy-btn-copied' : ''}`}
            onClick={handleCopy}
            aria-label={copied ? 'Copied!' : 'Copy IIN'}
            type="button"
          >
            {copied ? (
              <>
                <CheckIcon />
                <span>Copied!</span>
              </>
            ) : (
              <>
                <CopyIcon />
                <span>Copy</span>
              </>
            )}
          </button>
        )}
      </div>

      {masked && (
        <span className="iin-masked-hint">Click to copy full IIN</span>
      )}
    </div>
  );
}

// ==========================================================================
// IIN CARD COMPONENT
// ==========================================================================

/**
 * Card-style IIN display with visual styling
 */
export function IINCard({
  iin,
  userName,
  createdAt,
  blockchainAnchored,
  className = '',
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!iin) return;
    try {
      await navigator.clipboard.writeText(iin.replace(/-/g, ''));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const formattedDate = createdAt ? new Date(createdAt).toLocaleDateString() : null;

  const cardClasses = [
    'iin-card',
    blockchainAnchored && 'iin-card-verified',
    className,
  ].filter(Boolean).join(' ');

  return (
    <div className={cardClasses}>
      {/* Card Background Pattern */}
      <div className="iin-card-pattern" />
      
      {/* Header */}
      <div className="iin-card-header">
        <div className="iin-card-logo">
          <BrainIcon />
          <span>IAMONEAI</span>
        </div>
        {blockchainAnchored && (
          <div className="iin-card-verified-badge">
            <ChainIcon />
            <span>Verified</span>
          </div>
        )}
      </div>

      {/* IIN Number */}
      <div className="iin-card-number" onClick={handleCopy}>
        <code>{formatIIN(iin)}</code>
        {copied && <span className="iin-card-copied">Copied!</span>}
      </div>

      {/* Footer */}
      <div className="iin-card-footer">
        <div className="iin-card-holder">
          <span className="iin-card-label">Identity Holder</span>
          <span className="iin-card-value">{userName || 'User'}</span>
        </div>
        {formattedDate && (
          <div className="iin-card-date">
            <span className="iin-card-label">Created</span>
            <span className="iin-card-value">{formattedDate}</span>
          </div>
        )}
      </div>
    </div>
  );
}

// ==========================================================================
// IIN BADGE COMPONENT
// ==========================================================================

/**
 * Compact IIN badge for headers/navigation
 */
export function IINBadge({
  iin,
  onClick,
  className = '',
}) {
  if (!iin) return null;

  const shortIIN = `${iin.slice(0, 4)}...${iin.slice(-4)}`;

  return (
    <button
      className={`iin-badge ${className}`}
      onClick={onClick}
      aria-label="View IIN details"
    >
      <KeyIcon />
      <span>{shortIIN}</span>
    </button>
  );
}

// ==========================================================================
// HELPER FUNCTIONS
// ==========================================================================

/**
 * Format IIN with dashes and optional masking
 */
function formatIIN(iin, masked = false) {
  if (!iin) return '';
  
  // Remove existing dashes and clean
  const clean = iin.replace(/-/g, '').toUpperCase();
  
  // Split into groups of 4
  const groups = clean.match(/.{1,4}/g) || [];
  
  if (masked && groups.length === 4) {
    // Show first and last groups, mask middle
    return `${groups[0]}-••••-••••-${groups[3]}`;
  }
  
  return groups.join('-');
}

// ==========================================================================
// ICONS
// ==========================================================================

function CopyIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function BrainIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 2a4 4 0 0 1 4 4v1a4 4 0 0 1-4 4 4 4 0 0 1-4-4V6a4 4 0 0 1 4-4z" />
      <path d="M8 14a4 4 0 0 0-4 4v2h16v-2a4 4 0 0 0-4-4" />
      <circle cx="12" cy="12" r="10" />
    </svg>
  );
}

function ChainIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
  );
}

function KeyIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
    </svg>
  );
}

// ==========================================================================
// EXPORTS
// ==========================================================================

export default IINDisplay;